/** @jsxImportSource @emotion/react */
import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Sidebar } from "../../../shared";
import { Toast } from "primereact/toast";
import { Divider } from "primereact/divider";
import {
  formatFecha,
  Boton,
  Titulo,
  CampoInformacion,
  Formulario,
  accessAPI
} from "../../../shared";

export default function TarjetaDetallesProceso({
  proceso,
  onProcesoActualizado
}) {
  const [sidebarEditar, setSidebarEditar] = useState(false);
  const [formLoader, setFormLoader] = useState(false);
  const toast = useRef(null);
  const navigate = useNavigate();

  function handleGuardar(camposFormulario) {
    setFormLoader(true);
    accessAPI(
      "PUT",
      `admin/proceso/${proceso.id}`,
      camposFormulario,
      (respuesta) => {
        setFormLoader(false);
        setSidebarEditar(false);
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: "Proceso actualizado correctamente"
        });
        if (onProcesoActualizado) {
          onProcesoActualizado(respuesta);
        }
      },
      (error) => {
        setFormLoader(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al actualizar el proceso",
          sticky: true
        });
      }
    );
  }

  const formConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "nombrefantasia",
            tipoitemformularioid: 1,
            etiqueta: "Nombre del proceso",
            respuesta: proceso.nombrefantasia
          },
          {
            nombre: "clienteid",
            tipoitemformularioid: 5,
            etiqueta: "Cliente",
            opciones: "admin/cliente",
            sinOpcionInicial: true,
            respuesta: proceso.cliente.id
          },
          {
            nombre: "certificadotipoid",
            tipoitemformularioid: 5,
            etiqueta: "Tipo de certificado",
            opciones: "admin/config/certificadotipo",
            sinOpcionInicial: true,
            respuesta: proceso.certificadotipo.id
          },
          {
            nombre: "normaid",
            tipoitemformularioid: 5,
            etiqueta: "Norma",
            opciones: "admin/norma",
            sinOpcionInicial: true,
            respuesta: proceso.norma?.id,
            disabled: proceso.producto?.length > 0
          },
          {
            nombre: "esquemaid",
            tipoitemformularioid: 5,
            etiqueta: "Esquema",
            opciones: `admin/config/esquema?certificadotipoid=${proceso.certificadotipo.id}`,
            sinOpcionInicial: true,
            respuesta: proceso.esquema.id
          },
          {
            nombre: "sitioFabricaId",
            tipoitemformularioid: 5,
            etiqueta: "Sitio fábrica",
            opciones: "admin/proceso/fabricas",
            sinOpcionInicial: true,
            opcionVacia: true,
            queryparams: ["clienteid"],
            respuesta: proceso.sitioFabrica?.id
          },
          {
            nombre: "sitioFabricanteId",
            tipoitemformularioid: 5,
            etiqueta: "Sitio fabricante",
            opciones: "admin/proceso/fabricantes",
            sinOpcionInicial: true,
            opcionVacia: true,
            queryparams: ["clienteid"],
            respuesta: proceso.sitioFabricante?.id
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Guardar cambios",
        callback: handleGuardar,
        validar: true
      }
    ]
  };

  const clienteDisplay =
    proceso.cliente.nombrefantasia &&
    proceso.cliente.nombrefantasia !== proceso.cliente.razonsocial
      ? `${proceso.cliente.razonsocial} (${proceso.cliente.nombrefantasia})`
      : proceso.cliente.razonsocial;

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Card>
        <Titulo
          botones={
            <Boton icono negro callback={() => setSidebarEditar(true)}>
              <i className="pi pi-pencil" />
            </Boton>
          }
        >
          Detalles del proceso
        </Titulo>

        <div className="grid">
          <CampoInformacion nombreCampo="Cliente">
            <span
              css={{ cursor: "pointer", "&:hover": { color: "var(--principal)" } }}
              onClick={(e) => {
                const url = `/clientes/${proceso.cliente.id}/informacion`;
                e.metaKey || e.ctrlKey ? window.open(url, "_blank") : navigate(url);
              }}
            >
              {clienteDisplay}
            </span>
          </CampoInformacion>

          <CampoInformacion nombreCampo="Nombre del proceso">
            {proceso.nombrefantasia || "-"}
          </CampoInformacion>
          <Divider />

          <CampoInformacion nombreCampo="Tipo de certificado">
            {proceso.certificadotipo.nombre}
          </CampoInformacion>

          <CampoInformacion nombreCampo="Norma">
            {proceso.norma?.numero || "-"}
          </CampoInformacion>

          <CampoInformacion nombreCampo="Esquema">
            {proceso.esquema.etiqueta}
          </CampoInformacion>

          <Divider />
          <CampoInformacion nombreCampo="Nombre de la fábrica">
            {proceso.sitioFabrica?.nombre || "-"}
          </CampoInformacion>

          <CampoInformacion nombreCampo="Dirección de la fábrica">
            {proceso.sitioFabrica?.direccion || "-"}
          </CampoInformacion>

          <CampoInformacion nombreCampo="Nombre del fabricante">
            {proceso.sitioFabricante?.nombre || "-"}
          </CampoInformacion>

          <CampoInformacion nombreCampo="Dirección del fabricante">
            {proceso.sitioFabricante?.direccion || "-"}
          </CampoInformacion>

          <Divider />
          <CampoInformacion nombreCampo="Fecha de ingreso">
            {formatFecha(proceso.solicitudproceso?.fecha) || "-"}
          </CampoInformacion>

          {proceso.usuariocreador && (
            <CampoInformacion nombreCampo="Usuario creador">
              {`${proceso.usuariocreador.nombre} ${proceso.usuariocreador.apellido}`}
            </CampoInformacion>
          )}
        </div>
      </Card>

      <Sidebar
        visible={sidebarEditar}
        onHide={() => setSidebarEditar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Editar proceso</Titulo>
        <Formulario config={formConfig} loader={formLoader} />
      </Sidebar>
    </>
  );
}
